import { Dispatch } from '@reduxjs/toolkit';
import { showPopup } from 'Store/auth';

/**
 * Generic popup utility for showing temporary messages
 */
export class PopupUtils {
    private static timeoutIds = new Map<string, NodeJS.Timeout>();

    /**
     * Show a popup message with automatic closing
     * @param dispatch - Redux dispatch function
     * @param message - Message to display
     * @param duration - Duration in milliseconds (default: 3000)
     * @param messageType - Optional type for different styling/handling (default: 'info')
     */
    static showMessage(
        dispatch: Dispatch,
        message: string,
        duration: number = 3000,
        messageType: 'success' | 'error' | 'warning' | 'info' = 'info'
    ): void {
        // Clear any existing timeout for the same message type to prevent conflicts
        const existingTimeoutId = this.timeoutIds.get(messageType);
        if (existingTimeoutId) {
            clearTimeout(existingTimeoutId);
        }

        // Show the popup
        dispatch(showPopup({ msg: message, show: true }));

        // Set timeout to close the popup
        const timeoutId = setTimeout(() => {
            dispatch(showPopup({ msg: "", show: false }));
            this.timeoutIds.delete(messageType);
        }, duration);

        // Store the timeout ID
        this.timeoutIds.set(messageType, timeoutId);
    }

    /**
     * Show a success message
     */
    static showSuccess(dispatch: Dispatch, message: string, duration?: number): void {
        this.showMessage(dispatch, message, duration, 'success');
    }

    /**
     * Show an error message
     */
    static showError(dispatch: Dispatch, message: string, duration?: number): void {
        this.showMessage(dispatch, message, duration, 'error');
    }

    /**
     * Show a warning message
     */
    static showWarning(dispatch: Dispatch, message: string, duration?: number): void {
        this.showMessage(dispatch, message, duration, 'warning');
    }

    /**
     * Show an info message
     */
    static showInfo(dispatch: Dispatch, message: string, duration?: number): void {
        this.showMessage(dispatch, message, duration, 'info');
    }

    /**
     * Manually close the popup (useful for immediate dismissal)
     */
    static closePopup(dispatch: Dispatch, messageType: 'success' | 'error' | 'warning' | 'info' = 'info'): void {
        const timeoutId = this.timeoutIds.get(messageType);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.timeoutIds.delete(messageType);
        }
        dispatch(showPopup({ msg: "", show: false }));
    }

    /**
     * Clear all active popups and their timeouts
     */
    static clearAll(dispatch: Dispatch): void {
        this.timeoutIds.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        this.timeoutIds.clear();
        dispatch(showPopup({ msg: "", show: false }));
    }
}

/**
 * Hook-based utility for components using useDispatch
 */
export const usePopupUtils = () => {
    return {
        showMessage: PopupUtils.showMessage,
        showSuccess: PopupUtils.showSuccess,
        showError: PopupUtils.showError,
        showWarning: PopupUtils.showWarning,
        showInfo: PopupUtils.showInfo,
        closePopup: PopupUtils.closePopup,
        clearAll: PopupUtils.clearAll,
    };
};

/**
 * Simple function-based utilities (alternative approach)
 */
export const showPopupMessage = (
    dispatch: Dispatch,
    message: string,
    duration: number = 3000
): void => {
    dispatch(showPopup({ msg: message, show: true }));
    setTimeout(() => {
        dispatch(showPopup({ msg: "", show: false }));
    }, duration);
};

export const showSuccessMessage = (dispatch: Dispatch, message: string, duration?: number) => 
    showPopupMessage(dispatch, message, duration);

export const showErrorMessage = (dispatch: Dispatch, message: string, duration?: number) => 
    showPopupMessage(dispatch, message, duration);
